import Vue from "vue";
import axios from "axios";
import moment from "moment";
import momentTimezone from 'moment-timezone';
import config from "@core/config/app.config.js";
import constants from "@core/config/constants";
import { MarketingRepo } from "@core/repositories";
const { RESPONSE_CODES, IDENTITY_ERROR_MESSAGE } = constants;
import jwtDecode from "jwt-simple";
import store from "@/store";

import '@core/config/service-ability.config'

/**
 * Redirects user to specified url in param
 * @param {String} url Web url 
 */
export const redirectToUrl = (url) => {
  window.location.href = url;
};

/**
 * cancelAxiosRequest
 * @description Cancels ongoing axions http request
 * @param {String} message Error message to be thrown
 */
export const cancelAxiosRequest = (message = "Request cancelled.") => {
  throw new axios.Cancel(message);
};

export const getCookie = (key) => {
  return getCookieValue(key);
};

function getCookieValue(key){
  if(document.cookie.indexOf(`${key}=`) < 0){return null}
  let value = decodeURIComponent(document.cookie.split(`${key}=`)[1].split(';')[0]);
  value = value.replace(/[+]/g, ' ')
  try{
    return JSON.parse(value)
  }catch(e){
    return value
  }
}

export const verifyJTI = (token) => {
  let jti = getCookieValue(config.IDENTITY_JTI_COOKIE_NAME);
  if(token == undefined || token == '' || !jti){
    if(!jti && window.location.port == "8080"){
      return true;
    }
    return false;
  }
 
  try{
    let tokenInfo = jwtDecode.decode(token, '', true)
    return (tokenInfo.jti === jti);
  }catch(err){ return false; }
}

/**
 * Determines that api response code is success (200) or not
 * @returns {Boolean} is success or not
 */
export const isSuccess = (responseCode) => RESPONSE_CODES.success === responseCode

/**
 * hasProp
 * @description Determines whether a nested object contains a property or not
 * @returns {Boolean}
 */
export const hasProp = (data, prop) => {
  if (!data || Object.keys(data) <= 0) return false;
  return Object.getOwnPropertyNames(data).includes(prop);
};

export const keycloakObj = () => {
  const { authServerUrl, realm, authenticated } = Vue.prototype.$keycloak;
  return {
    baseURL: `${authServerUrl}realms/${realm}/`,
    authenticated
  };
}

export const refreshToken = async() =>{
  const keycloak = Vue.prototype.$keycloak;
      try{
        await keycloak.updateToken(process.env.VUE_APP_KEYCLOAK_REFRESH_TOKEN)
      }catch(err){
        toReauth();
      }
}


export const toReauth = async() => {
  const keycloak = Vue.prototype.$keycloak;
  if(keycloak)
    await keycloak.logout({logoutMethod:null});
  store.dispatch("auth/logout")
}

export const isDevEnv = () => {
  const { NODE_ENV } = process.env;
  return NODE_ENV === "development" || window.location.port == "8080";
};

export const verifyAccountCluster = (account, return_url=false) => {
  const cluster = account.cluster;
  let base_url = '';
  switch(cluster){
    case "api-ca1":
    case 'canada': 
      base_url = `https://api-ca1.maropost.com`;
      break;
    case "api-eu1":
    case 'europe':
      base_url = `https://api-eu1.maropost.com`;
      break;
    default:
      base_url = `https://${cluster}-web.maropost.com`;
  }

  if(return_url === true) return `${base_url}`

  /**
   * If return_url is SET true
   * 01. base_url will be returned 
   * 02. RAILS_BASE_URL will not be set.
   */
  config.RAILS_BASE_URL = base_url;
  MarketingRepo.defaults.baseURL = `${config.RAILS_BASE_URL}/v2/${account.id}`;
}

export const extractCluster = (url) =>{
  try{
    let obj = new URL(url);
    return obj.origin.replace(/\b(?:https:\/\/|-web|.maropost.com)\b/gi,'');
  }
  catch(err){
    return false;
  }
}

export const sanitiseAccountList = (accounts) => {
  if(!accounts || accounts == null || accounts == undefined) return [];
  return accounts.filter(item=>item!=null).map(item => {
    item.uid = item.id;
    return item;
  })
}

export const _debounce = (func, delay) => {
  let debounceTimer
  return function () {
    const context = this
    const args = arguments
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => func.apply(context, args), delay)
  }
}

export const userProductAccess = (userResponseRoles) => {
  // Check if the user has "service" access
  const hasServiceAccess = !!userResponseRoles.some((role) => role === "service" || role.startsWith("service/"));
  // Check if the user has "marketing" access
  const hasMarketingAccess = !!userResponseRoles.find((role) =>
      role !== "account" && role !== "accounts_read_only" && role !== "admin" && role !== "user" && role !== "segments" && !role.startsWith("service")
  );
  var userRoleAccess = {
    haveMarketingRole: hasMarketingAccess,
    haveServiceRole: hasServiceAccess,
  };
  return userRoleAccess;
};

export const checkRolesAndRedirect = function(account, userAccess) {
  let platform = process.env.VUE_APP_PRODUCT
  if ((!account.enable_marketing || !userAccess.haveMarketingRole) && (!account.enable_service || !userAccess.haveServiceRole)) {
    return false;
  }
  
  switch(platform) {
    case "Marketing":
      if ((!account.enable_marketing || !userAccess.haveMarketingRole) && (account.enable_service || userAccess.haveServiceRole)) {
        if(!window.location.href.includes('/service')){
          window.location.href = window.location.origin + `/service/${account.id}/dashboard`;
        }
      }
      break;
    case "Service":
      if ((account.enable_marketing || userAccess.haveMarketingRole) && (!account.enable_service || !userAccess.haveServiceRole)) {
        if(!window.location.href.includes('/accounts')){
          window.location.href = window.location.origin + `/accounts/${account.id}/dashboard`;
        }
      }
      break;
  }
  return true; 
};
export function getSelectedFilterDayDate(days) {
  const FORMAT = "YYYY-MM-DD";
  const dateType = days == 365 ? "year" : "month";

  const created_on_gteq = moment()
    .startOf(dateType)
    .format(FORMAT);

  const created_on_lteq = moment()
    .format(FORMAT);

  return {
    created_on_gteq,
    created_on_lteq
  };
}

export function replaceUnderscore(text = "") {
  return text.includes("_") ? text.replace(/_/i, " ") : text;
}

/*** manage date via selected timezone */
export const manageParams = (data)=>{
  let iterate, type;
  if(data instanceof FormData){
    const formDataArray = Array.from(data);
    iterate = formDataArray;
    type = 'FormData';
  }
  else if(typeof data == 'object'){
    iterate = Object.entries(data);
    type = 'Object';
  }

  iterate.forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      manageParams(value);
    } else {
      const offset = momentTimezone.tz(store.getters["auth/preferences"].time_zone).format('Z');
      if(validDate(value) &&
        (offset !== "-04:00" || offset !== "-05:00"))
      {
        value = manageDate(value);
          
        /*** Create a moment in User Selected timezone */
        const finalDate = momentTimezone.tz(value, store.getters["auth/preferences"].time_zone);
        
        /*** Convert to Toronto timezone */
        const formatDate = finalDate.clone().tz("America/Toronto").format();
        
        if(type == 'Object')
          data[key] = formatDate;
        else if(type == 'FormData'){
          data.set(key, formatDate);
        }
      }
    }
  });

  return data;
};

/*** manage 24 hr format */
export const manageDate = (date)=>{
  //25/09/2024 15:00
  const dateTimeRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4} (0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
  if(dateTimeRegex.test(date)){
    return momentTimezone(date, "DD/MM/YYYY HH:mm").format("YYYY-MM-DDTHH:mm:ss");
  }
  else{
    //June 26, 2024 04:30 AM
    const datePattern1 = /^([A-Za-z]+) (\d{1,2}), (\d{4}) (\d{1,2}):(\d{2}) (AM|PM)$/.test(date);
    //2024-05-31 09:15 AM
    const datePattern2 = /^(20\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\s(0[1-9]|1[0-2]):([0-5][0-9])\s(AM|PM)$/.test(date);
    let dateFormat;
    if(datePattern1)
      dateFormat = "MMMM DD, YYYY hh:mm A";
    else if(datePattern2)
      dateFormat = "YYYY-MM-DD hh:mm A";
    return momentTimezone(date, dateFormat).format("YYYY-MM-DDTHH:mm:ss");
  }
}

export const validDate = (value)=>{
  //June 26, 2024 04:30 AM
  const datePattern1 = /^([A-Za-z]+) (\d{1,2}), (\d{4}) (\d{1,2}):(\d{2}) (AM|PM)$/;
  //2024-05-31 09:15 AM
  const datePattern2 = /^(20\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\s(0[1-9]|1[0-2]):([0-5][0-9])\s(AM|PM)$/;
  //25/09/2024 15:00
  const datePattern3 = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4} (0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;

  if (datePattern1.test(value) || datePattern2.test(value) || datePattern3.test(value))
    return true;
  else
    return false;
};

export const createDownloadLink = downloadLink =>{
  refreshToken();
  downloadLink = new URL(downloadLink)
  downloadLink.searchParams.set("MP-ID-TOKEN",Vue.prototype.$keycloak.token)
  console.log(downloadLink.toString())
  return downloadLink.toString()
}

export const mergeObjects = (current, updates) => {
  if(Array.isArray(updates)){
    current = []
  }
  if(updates){  
    for (let key of Object.keys(updates)) {
      if (!current.hasOwnProperty(key) || typeof updates[key] !== 'object'){
        current[key] = updates[key];
      } 
      else {
        current[key] = mergeObjects(current[key], updates[key]);
      }
    }
  }
  return current;
}